import { createClient } from "@supabase/supabase-js";
import { logger } from "./logger";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as
  | string
  | undefined;

if (!supabaseUrl || !supabaseAnonKey) {
  logger.error(
    "❌ [Orderbook] Supabase environment variables are not set!\n" +
      "Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your hosting platform.\n" +
      "Current URL:", supabaseUrl ?? "MISSING", "\n" +
      "Current Key:", supabaseAnonKey ? "SET" : "MISSING"
  );
} else {
  logger.log("✅ [Orderbook] Supabase configured:", supabaseUrl);
}

const finalUrl = supabaseUrl ?? "http://localhost:54321";
const finalKey = supabaseAnonKey ?? "public-anon-key";

// Export the anon key for function calls
export const SUPABASE_ANON_KEY = finalKey;

// Configure Supabase client with proper auth settings
export const supabase = createClient(finalUrl, finalKey, {
  auth: {
    autoRefreshToken: true, // Automatically refresh expired tokens
    persistSession: true, // Persist session in localStorage
    detectSessionInUrl: true, // Detect session from URL (for OAuth callbacks)
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
    storageKey: "sb-auth-token", // Custom storage key
  },
  global: {
    headers: {
      "x-client-info": "orderbook-webapp",
    },
  },
});

// Helper to ensure we have a valid session before making authenticated requests
export async function ensureAuthenticatedSession(): Promise<boolean> {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error) {
      logger.error("❌ Session error:", error);
      return false;
    }
    
    if (!session) {
      logger.warn("⚠️ No session found");
      return false;
    }
    
    // Check if token is expired or about to expire (within 60 seconds)
    const expiresAt = session.expires_at;
    if (expiresAt) {
      const expiresIn = expiresAt - Math.floor(Date.now() / 1000);
      if (expiresIn < 60) {
        logger.log("🔄 Token expiring soon, refreshing...");
        const { data: { session: newSession }, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError) {
          logger.error("❌ Failed to refresh session:", refreshError);
          return false;
        }
        if (!newSession) {
          logger.warn("⚠️ No session after refresh");
          return false;
        }
        logger.log("✅ Session refreshed");
      }
    }
    
    return true;
  } catch (error) {
    logger.error("❌ Error ensuring session:", error);
    return false;
  }
}
